import { CircleAlert, CircleCheck, FileQuestion, Info, TriangleAlert, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { ApiError } from '../api/client';
import { LinkButton } from './Button';

export const Spinner = ({ large }: { large?: boolean }) => (
  <span className={`spinner${large ? ' lg' : ''}`} role="status" aria-label="Loading" />
);

export function PageLoading() {
  return (
    <div className="empty" style={{ minHeight: '40vh' }}>
      <Spinner large />
    </div>
  );
}

export function EmptyState({
  icon: Icon = Info,
  title,
  children,
  action,
}: {
  icon?: LucideIcon;
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <Icon size={48} strokeWidth={1.5} aria-hidden />
      <div className="title">{title}</div>
      {children && (
        <p className="small" style={{ maxWidth: 420 }}>
          {children}
        </p>
      )}
      {action}
    </div>
  );
}

const ICONS = { error: CircleAlert, warning: TriangleAlert, info: Info, success: CircleCheck };

export function Alert({
  tone = 'info',
  title,
  children,
  role,
}: {
  tone?: 'error' | 'warning' | 'info' | 'success';
  title?: ReactNode;
  children?: ReactNode;
  role?: 'alert' | 'status';
}) {
  const Icon = ICONS[tone];
  return (
    <div className={`alert alert-${tone}`} role={role ?? (tone === 'error' ? 'alert' : 'status')}>
      <Icon size={16} aria-hidden />
      <div className="alert-body">
        {title && <strong style={{ fontWeight: 500 }}>{title}</strong>}
        {children}
      </div>
    </div>
  );
}

/** Explains failed actions, including validation details and recovery guidance. */
export function ErrorAlert({ error, title }: { error: unknown; title?: string }) {
  if (!error) return null;
  if (error instanceof ApiError) {
    const unrelated = error.details.filter((d) => d.message !== error.message);
    return (
      <Alert tone="error" title={title ?? error.message}>
        {title && <span>{error.message}</span>}
        {unrelated.length > 0 && (
          <ul>
            {unrelated.map((d, i) => (
              <li key={i}>{d.message}</li>
            ))}
          </ul>
        )}
        {error.recovery && <span>{error.recovery}</span>}
      </Alert>
    );
  }
  return (
    <Alert tone="error" title={title ?? 'Something went wrong'}>
      {error instanceof Error ? error.message : 'Try again.'}
    </Alert>
  );
}

/** Missing and inaccessible records render the same screen at the requested URL (screens §2). */
export function PageUnavailable() {
  return (
    <div className="empty" style={{ minHeight: '60vh', gap: 12 }}>
      <FileQuestion size={48} strokeWidth={1.5} aria-hidden />
      <h1 className="page-title">Page unavailable</h1>
      <p className="muted" style={{ maxWidth: 420 }}>
        This page doesn't exist, or you don't have access to it. Check the link, or ask the person who shared
        it.
      </p>
      <LinkButton to="/projects" variant="secondary">
        Go to projects
      </LinkButton>
    </div>
  );
}

/** Renders loading, unavailable and error states for a query; children render with data. */
export function QueryView<T>({
  query,
  children,
}: {
  query: { data: T | undefined; error: unknown; isPending: boolean; refetch: () => unknown };
  children: (data: T) => ReactNode;
}) {
  if (query.isPending) return <PageLoading />;
  if (query.error) {
    if (query.error instanceof ApiError && (query.error.status === 404 || query.error.status === 403))
      return <PageUnavailable />;
    return (
      <div className="stack" style={{ maxWidth: 640 }}>
        <ErrorAlert error={query.error} title="This page could not load" />
        <div>
          <button className="btn btn-secondary sm" onClick={() => query.refetch()}>
            Retry
          </button>
        </div>
      </div>
    );
  }
  return <>{children(query.data as T)}</>;
}
